import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'
import { checkoutReferralIdAtom } from '@/core/checkout/checkout.store'
import { isLoadingOmanFormatAtom, isLoadingOmanGeoAtom, omanFormatDataAtom } from '../../../stores/oman.store'
import { useOmanCartActions } from '../../../hooks/useOmanCartActions'
import { useOmanGeo } from '../../../hooks/useOmanGeo'
import { getRouteFromPath } from '../checkout.helpers'

interface OmanCheckoutActions {
    isAuthenticated: boolean
    loadFormatData: (uuid: string) => void
    createUuidAndProceed: (reload?: boolean) => void
    createGuestUuidAndProceed: () => void
    validateAndProceed: (referral: string) => void
    loading: boolean
    errors: string[]
    setErrors: (errors: string[]) => void
}

export const useOmanCheckoutInit = ({
    isAuthenticated,
    loadFormatData,
    createUuidAndProceed,
    createGuestUuidAndProceed,
    validateAndProceed,
    loading,
    errors,
    setErrors
}: OmanCheckoutActions) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const setReferralId = useSetAtom(checkoutReferralIdAtom)
    const isLoadingFormat = useAtomValue(isLoadingOmanFormatAtom)
    const isLoadingGeo = useAtomValue(isLoadingOmanGeoAtom)
    const formatData = useAtomValue(omanFormatDataAtom)

    const cartActions = useOmanCartActions()

    const hasRequestedUuidRef = useRef(false)
    const loadedFormatUuidRef = useRef<string | null>(null)
    const hasValidatedReferralRef = useRef(false)
    const formatDataWasLoadedRef = useRef(false)
    const hasSwitchedToMemberRef = useRef(false)

    const [initialized, setInitialized] = useState(false)

    useOmanGeo()

    const route = getRouteFromPath(location.pathname)

    const initCheckout = useCallback(() => {
        hasRequestedUuidRef.current = true
        setErrors([])
        if (isAuthenticated) {
            createUuidAndProceed()
        } else {
            createGuestUuidAndProceed()
        }
    }, [isAuthenticated, createUuidAndProceed, createGuestUuidAndProceed, setErrors])

    // Redirect legacy /checkout to correct sub-route
    useEffect(() => {
        if (route !== null) return
        navigate(isAuthenticated ? '/checkout/shipping' : '/checkout/create-account', { replace: true })
    }, [route, isAuthenticated, navigate])

    // Guest logs in on the create-account step → switch to the member flow.
    // createUuidAndProceed(true) creates the shop session (cart is still in memory)
    // then HARD-reloads to /checkout/shipping?uuid=… — the reloaded page loads the
    // format by uuid (the cart atom isn't persisted, so the uuid must carry the
    // order). A reload is reliable; SPA navigation raced with the auth update and
    // sometimes left the page stuck on create-account. Ref-guarded to fire once.
    useEffect(() => {
        if (route !== 'create-account' || !isAuthenticated) return
        if (hasSwitchedToMemberRef.current) return
        hasSwitchedToMemberRef.current = true
        createUuidAndProceed(true)
    }, [route, isAuthenticated, createUuidAndProceed])

    // Load format / validate referral / init checkout
    useEffect(() => {
        if (route === null) return
        const uuid = searchParams.get('uuid')
        const referral = searchParams.get('referral') ?? searchParams.get('customerId')
        if (referral) setReferralId(referral)
        if (uuid) {
            if (loadedFormatUuidRef.current === uuid) return
            loadedFormatUuidRef.current = uuid
            loadFormatData(uuid)
        } else if (referral) {
            if (hasValidatedReferralRef.current) return
            hasValidatedReferralRef.current = true
            validateAndProceed(referral)
        } else {
            if (hasRequestedUuidRef.current) return
            initCheckout()
        }
    }, [route, searchParams, setReferralId, loadFormatData, validateAndProceed, initCheckout])

    // Mark initialized once all loading settles
    useEffect(() => {
        if (initialized) return
        if (isLoadingFormat || isLoadingGeo || loading) return
        const urlUuid = searchParams.get('uuid')
        if (!urlUuid) return
        if (!formatData || formatData.uuid !== urlUuid) return
        setInitialized(true)
    }, [initialized, isLoadingFormat, isLoadingGeo, loading, formatData, searchParams])

    // Navigate home if format data disappears after load
    useEffect(() => {
        if (formatData) {
            formatDataWasLoadedRef.current = true
            return
        }
        if (!formatDataWasLoadedRef.current || isLoadingFormat) return
        navigate('/')
    }, [formatData, isLoadingFormat, navigate])

    const retryCheckout = () => {
        hasRequestedUuidRef.current = false
        loadedFormatUuidRef.current = null
        initCheckout()
    }

    return {
        initialized,
        cartActions,
        route,
        isLoadingFormat,
        isLoadingGeo,
        formatData,
        errors,
        retryCheckout
    }
}
